import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@nesso/ui/primitives/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@nesso/ui/primitives/form';
import { Input } from '@nesso/ui/primitives/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@nesso/ui/primitives/select';
import { SelectField } from '@nesso/ui/primitives/select-field';

import { licenseTypeOptions, stateOptions, taxonomyCodeOptions } from '@/entities/license';
import { type AddLicenseInput, addLicenseSchema } from '@/entities/practice-member';

type Props = {
    defaultValues?: Partial<AddLicenseInput>;
    onSubmit: (data: AddLicenseInput) => void;
    isSubmitting?: boolean;
};

// `taxonomyCode` must accept `''` too: Radix needs it to show the "cleared" placeholder while
// staying controlled, but a stored `undefined` would have react-hook-form resolve the field back
// to `defaultValues` — on an edit, the very taxonomy being cleared.
const licenseFormSchema = addLicenseSchema.extend({
    taxonomyCode: addLicenseSchema.shape.taxonomyCode.or(z.literal('')),
});

type LicenseFormValues = z.infer<typeof licenseFormSchema>;

// Shared by add and edit, the edit path arriving through `defaultValues`. Both validate against
// addLicenseSchema: practiceMemberUpdateLicense carries the same four fields because an edit is a
// full replacement, so a separate resolver would only restate it.
export function ProfileLicenseForm({ defaultValues, onSubmit, isSubmitting }: Props) {
    const form = useForm<LicenseFormValues>({
        resolver: zodResolver(licenseFormSchema),
        defaultValues: { number: '', ...defaultValues },
    });

    // `''` is only the form's "cleared" stand-in — convert back to `undefined` here so the PATCH
    // body omits the key entirely instead of clearing nothing.
    const handleSubmit = form.handleSubmit((data) =>
        onSubmit({ ...data, taxonomyCode: data.taxonomyCode || undefined }),
    );

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="flex flex-col items-start gap-6">
                <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                        <FormItem className="w-full gap-3">
                            <FormLabel>License type</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                // Radix's Select bridges to a hidden native <select> for form
                                // semantics; leaving this undefined until the first pick makes
                                // it go from uncontrolled to controlled. `''` keeps it always
                                // controlled without matching any SelectItem, so the placeholder
                                // still shows and required validation still fires on submit.
                                value={field.value ?? ''}
                                disabled={isSubmitting}
                            >
                                <FormControl>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select license type" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {licenseTypeOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                        <FormItem className="w-full gap-3">
                            <FormLabel>License number</FormLabel>
                            <FormControl>
                                <Input {...field} disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                        <FormItem className="w-full gap-3">
                            <FormLabel>State</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value ?? ''}
                                disabled={isSubmitting}
                            >
                                <FormControl>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {stateOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                {/* SelectField renders its own label via Field, so no FormItem/FormLabel here.
                No `error` prop either — taxonomy is optional and never blocks a save. */}
                <Controller
                    control={form.control}
                    name="taxonomyCode"
                    render={({ field }) => (
                        <SelectField
                            {...field}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            id={field.name}
                            label="Taxonomy (optional)"
                            placeholder="Select taxonomy"
                            options={taxonomyCodeOptions}
                            clearable
                            disabled={isSubmitting}
                        />
                    )}
                />

                <Button type="submit" size="lg" disabled={isSubmitting}>
                    Save
                </Button>
            </form>
        </Form>
    );
}
